import DOMPurify from 'dompurify';

/**
 * XSS Protection Utility
 * Provides comprehensive XSS protection for frontend inputs
 * Using DOMPurify library for sanitization
 */

export interface SanitizationOptions {
    allowedTags?: string[];
    allowedAttributes?: string[];
    stripTags?: boolean;
    preserveNewlines?: boolean;
}

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export const sanitizeHtml = (
    dirty: string, 
    options: SanitizationOptions = {}
): string => {
    if (!dirty || typeof dirty !== 'string') {
        return '';
    }

    const config: any = {
        ALLOWED_TAGS: options.allowedTags || [],
        ALLOWED_ATTR: options.allowedAttributes || [],
        KEEP_CONTENT: !options.stripTags,
        ALLOW_DATA_ATTR: false,
        ALLOW_UNKNOWN_PROTOCOLS: false,
        SANITIZE_DOM: true,
        FORBID_ATTR: [
            'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur',
            'onchange', 'onsubmit', 'onreset', 'onselect', 'onunload'
        ],
        FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button']
    };

    // Additional security for completely stripping HTML
    if (options.stripTags) {
        config.ALLOWED_TAGS = [];
        config.KEEP_CONTENT = true;
    }

    let sanitized = DOMPurify.sanitize(dirty, config);

    // Convert TrustedHTML to string
    const sanitizedString = String(sanitized);

    // Preserve newlines if requested
    if (options.preserveNewlines && !options.stripTags) {
        return sanitizedString.replace(/\n/g, '<br>');
    }

    return sanitizedString;
};

/**
 * Sanitize plain text input (no HTML allowed)
 * Most secure option for user inputs
 */
export const sanitizeText = (input: string): string => {
    if (!input || typeof input !== 'string') {
        return '';
    }

    return sanitizeHtml(input, { stripTags: true })
        .trim()
        // Remove any remaining HTML entities
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#x27;/g, "'")
        .replace(/&#x2F;/g, '/')
        // Remove null bytes and control characters
        .replace(/\0/g, '')
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
        // Normalize unicode
        .normalize('NFC');
};

/**
 * Sanitize email input with specific validation
 */
export const sanitizeEmail = (email: string): string => {
    if (!email || typeof email !== 'string') {
        return '';
    }

    return sanitizeText(email.toLowerCase())
        .replace(/[^a-z0-9@._+-]/g, '') // Only allow valid email characters
        .slice(0, 100); // Limit length
};

/**
 * Sanitize company/user name inputs
 */
export const sanitizeName = (name: string): string => {
    if (!name || typeof name !== 'string') {
        return '';
    }

    return sanitizeText(name)
        // Allow only letters, spaces, hyphens, apostrophes, and periods
        .replace(/[^a-zA-Z\s\-'.]/g, '')
        .replace(/\s+/g, ' ') // Normalize multiple spaces
        .slice(0, 100) // Limit length
        .trim();
};

/**
 * Sanitize company names without stripping valid symbols/numbers.
 * Keep user intent while still removing unsafe control/HTML artifacts.
 */
export const sanitizeCompanyName = (name: string): string => {
    if (!name || typeof name !== 'string') {
        return '';
    }

    return sanitizeText(name).slice(0, 100);
};

/**
 * Sanitize URL inputs
 */
export const sanitizeUrl = (url: string): string => {
    if (!url || typeof url !== 'string') {
        return '';
    }

    const sanitized = sanitizeText(url);
    
    // Only allow http/https URLs
    if (!sanitized.match(/^https?:\/\//i)) {
        return '';
    }

    try {
        const urlObj = new URL(sanitized);
        // Additional validation
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            return '';
        }
        return urlObj.toString();
    } catch {
        return '';
    }
};

/**
 * Sanitize phone number inputs
 */
export const sanitizePhone = (phone: string): string => {
    if (!phone || typeof phone !== 'string') {
        return '';
    }

    return sanitizeText(phone)
        .replace(/[^0-9+\-\s()]/g, '') // Only allow phone number characters
        .slice(0, 20); // Limit length
};

/**
 * Create a sanitized form data handler
 * Automatically sanitizes form values before submission
 */
export const createSanitizedFormHandler = (
    originalHandler: (values: any) => void,
    fieldSanitizers: Record<string, (value: string) => string> = {}
) => {
    return (values: any) => {
        const sanitizedValues: any = {};
        
        Object.keys(values).forEach(key => {
            const value = values[key];
            
            if (typeof value === 'string') {
                // Use specific sanitizer if provided, otherwise use general text sanitizer
                const sanitizer = fieldSanitizers[key] || sanitizeText;
                sanitizedValues[key] = sanitizer(value);
            } else {
                // Non-string values (booleans, numbers, etc.) pass through
                sanitizedValues[key] = value;
            }
        });
        
        originalHandler(sanitizedValues);
    };
};

/**
 * Validate and sanitize form inputs with common patterns
 */
export const getFormSanitizers = () => ({
    email: sanitizeEmail,
    companyName: sanitizeCompanyName,
    adminName: sanitizeName,
    companyAddress: sanitizeText,
    companyPhone: sanitizePhone,
    companyWebsite: sanitizeUrl,
    companyDomain: (domain: string) => sanitizeText(domain).replace(/[^a-zA-Z0-9.-]/g, '').toLowerCase(),
    password: (password: string) => password, // Don't sanitize passwords (they need all characters)
    confirmPassword: (password: string) => password
});

/**
 * Check for potentially malicious patterns in input
 */
export const detectSuspiciousPatterns = (input: string): string[] => {
    if (!input || typeof input !== 'string') {
        return [];
    }

    const suspiciousPatterns = [
        { pattern: /<script/i, description: 'Script tag detected' },
        { pattern: /javascript:/i, description: 'JavaScript URL detected' },
        { pattern: /on\w+\s*=/i, description: 'Event handler attribute detected' },
        { pattern: /data:text\/html/i, description: 'Data URL with HTML detected' },
        { pattern: /vbscript:/i, description: 'VBScript URL detected' },
        { pattern: /<iframe/i, description: 'Iframe tag detected' },
        { pattern: /<object/i, description: 'Object tag detected' },
        { pattern: /<embed/i, description: 'Embed tag detected' }
    ];

    const detected: string[] = [];
    
    suspiciousPatterns.forEach(({ pattern, description }) => {
        if (pattern.test(input)) {
            detected.push(description);
        }
    });

    return detected;
};

/**
 * Enhanced input validator that combines XSS protection with validation
 */
export const createSecureValidator = (
    validator: (value: string) => boolean | string,
    sanitizer: (value: string) => string = sanitizeText
) => {
    return (value: string) => {
        // First check for suspicious patterns
        const suspiciousPatterns = detectSuspiciousPatterns(value);
        if (suspiciousPatterns.length > 0) {
            return `Security warning: ${suspiciousPatterns[0]}`;
        }

        // Sanitize the input
        const sanitized = sanitizer(value);
        
        // Run the original validator on sanitized input
        const validationResult = validator(sanitized);
        
        return validationResult;
    };
};
